/**
 * 4성부 화성 채점 — 분석 결과(성부진행 위반·불완전 화음) + 선택적 문제 진행 대조.
 * 100점 만점, 감점식.
 */
import { VOICE_NAMES, getBeatsPerMeasure, pitchToMidi, BEAT_VALUES } from './musicTheory.js'

const DEDUCT = {
  voiceLeading: 8,
  incomplete: 5,
  emptyBeat: 6,
  progressionMismatch: 6,
  missingVoiceCoverage: 10,
}

function normalizeRoman(sym) {
  if (sym == null) return ''
  let s = String(sym).trim()
  if (!s || s === '—' || s === '-' || s === '?') return ''
  if (s.startsWith('incomplete')) return ''
  // 기호 정규화: 위첨자·슬래시 공백 제거
  s = s.replace(/\s+/g, '')
  return s
}

function romansRoughlyEqual(a, b) {
  const x = normalizeRoman(a)
  const y = normalizeRoman(b)
  if (!x || !y) return false
  if (x === y) return true
  // V7 vs V, ii7 vs ii 등 확장만 다른 경우 부분 인정하지 않음 — 엄격 일치
  return false
}

function collectBeatEntries(analysisResult) {
  const out = []
  for (const m of analysisResult?.measures ?? []) {
    if (Array.isArray(m.beats) && m.beats.length) {
      for (const b of m.beats) {
        out.push({
          measure: m.measure,
          beat: b.beat,
          chordSymbol: b.chordSymbol,
          issues: b.voiceLeadingIssues ?? [],
        })
      }
    } else {
      out.push({
        measure: m.measure,
        beat: 1,
        chordSymbol: m.chordSymbol,
        issues: m.voiceLeadingIssues ?? [],
      })
    }
  }
  return out
}

/** 각 성부가 실제 음(쉼표 제외)으로 채운 박수 */
function voiceSoundingBeats(score) {
  const ratios = {}
  let maxSounding = 0
  for (const v of VOICE_NAMES) {
    const notes = score?.[v] ?? []
    let sounding = 0
    for (const n of notes) {
      const d = BEAT_VALUES[n.duration] ?? 1
      if (!n.isRest && n.pitch && pitchToMidi(n.pitch) != null) sounding += d
    }
    ratios[v] = sounding
    maxSounding = Math.max(maxSounding, sounding)
  }
  return { ratios, maxSounding }
}

/**
 * @param {object} analysisResult analyzeHarmonyPayload 결과
 * @param {object} [opts]
 * @param {object} [opts.score] SATB 악보
 * @param {string} [opts.timeSignature]
 * @param {object|null} [opts.progression] generateHarmonyProgression 결과 (풀이 문제)
 * @returns {{
 *   score: number,
 *   maxScore: 100,
 *   deductions: Array<{reason: string, points: number}>,
 *   summary: string,
 *   issueCount: number,
 * }}
 */
export function gradeHarmony(analysisResult, opts = {}) {
  const { score = null, timeSignature = '4/4', progression = null } = opts
  const deductions = []
  let penalty = 0

  const beats = collectBeatEntries(analysisResult)
  let issueCount = 0

  for (const b of beats) {
    for (const iss of b.issues) {
      const text = typeof iss === 'string' ? iss : (iss?.message || String(iss))
      issueCount += 1
      const pts = DEDUCT.voiceLeading
      penalty += pts
      deductions.push({
        reason: `마디 ${b.measure} 박 ${b.beat}: ${text}`,
        points: pts,
      })
    }
    const roman = normalizeRoman(b.chordSymbol)
    if (!roman) {
      const pts = DEDUCT.incomplete
      penalty += pts
      deductions.push({
        reason: `마디 ${b.measure} 박 ${b.beat}: 화음 판정 불가·불완전`,
        points: pts,
      })
    }
  }

  if (score) {
    const { ratios, maxSounding } = voiceSoundingBeats(score)
    const targetFromProgression = progression?.measureCount
      ? progression.measureCount * getBeatsPerMeasure(progression.timeSignature || timeSignature)
      : 0
    const target = Math.max(maxSounding, targetFromProgression)
    for (const v of VOICE_NAMES) {
      const sounding = ratios[v]
      if (target > 0 && sounding < target * 0.85) {
        const pts = DEDUCT.missingVoiceCoverage
        penalty += pts
        const label = { soprano: '소프라노', alto: '알토', tenor: '테너', bass: '베이스' }[v]
        deductions.push({
          reason: `${label}: 음이 부족합니다 (채워야 할 길이의 약 ${Math.round((sounding / target) * 100)}%)`,
          points: pts,
        })
      }
    }
  }

  if (progression?.slots?.length && beats.length) {
    const span = progression.chordSpanBeats || 1
    const bpp = getBeatsPerMeasure(progression.timeSignature || timeSignature)
    const seenSlots = new Set()
    for (const b of beats) {
      const tCenter = ((b.measure ?? 1) - 1) * bpp + ((b.beat ?? 1) - 1) + 0.5
      const slotIndex = Math.min(
        progression.slots.length - 1,
        Math.max(0, Math.floor(tCenter / span)),
      )
      if (seenSlots.has(slotIndex)) continue
      seenSlots.add(slotIndex)
      const expected = progression.slots[slotIndex]?.roman
      const got = b.chordSymbol
      if (expected && normalizeRoman(got) && !romansRoughlyEqual(got, expected)) {
        const pts = DEDUCT.progressionMismatch
        penalty += pts
        deductions.push({
          reason: `화음 ${slotIndex + 1}: 기대 ${expected}, 분석 ${normalizeRoman(got) || '—'}`,
          points: pts,
        })
      }
    }
  }

  const finalScore = Math.max(0, Math.min(100, Math.round(100 - penalty)))
  let summary
  if (finalScore >= 90) summary = '우수 — 성부진행·화음이 대체로 올바릅니다.'
  else if (finalScore >= 70) summary = '양호 — 일부 위반이 있습니다. 감점 항목을 확인하세요.'
  else if (finalScore >= 40) summary = '보완 필요 — 병행·불완전 화음·빈 성부를 점검하세요.'
  else summary = '미흡 — 화성·성부진행을 다시 작성해 보세요.'

  return {
    score: finalScore,
    maxScore: 100,
    deductions,
    summary,
    issueCount,
  }
}
